/**
 * Quota math helpers per docs/billing-and-pricing-product-guide.md §4
 */

import { BillingSubscription } from '@/types/billing'

/**
 * totalQuota = freeQuota (for free_quota_only) OR usableQuota + (freeQuota ?? 0) - (freeQuotaUsed ?? 0)
 * availableSlots = totalQuota - usedSlots
 * canActivate = availableSlots > 0
 */
export interface QuotaDisplay {
  usedSlots: number
  confirmedQuota: number
  usableQuota: number
  scheduledQuota: number
  freeQuota: number
  freeQuotaUsed: number
  totalQuota: number
  availableSlots: number
  canActivate: boolean
  /** Subscribed (paid) quota for display */
  subscribed: number
}

export function getQuotaDisplay(sub: BillingSubscription): QuotaDisplay {
  const usedSlots = sub.usedSlots ?? 0
  const confirmedQuota = sub.confirmedQuota ?? 0
  const usableQuota = sub.usableQuota ?? confirmedQuota
  const scheduledQuota = sub.scheduledQuota ?? confirmedQuota
  const freeQuota = sub.freeQuota ?? 0
  const freeQuotaUsed = sub.freeQuotaUsed ?? 0

  // For free-quota-only subscriptions the API sets usableQuota === freeQuota (both
  // represent the same free slots). Adding them together would double-count.
  // Use freeQuota directly as the total; for paid/mixed subscriptions use the
  // standard formula: paid usable quota + available free quota.
  const totalQuota =
    sub.status === 'free_quota_only'
      ? freeQuota
      : usableQuota + freeQuota - freeQuotaUsed
  const availableSlots = Math.max(0, totalQuota - usedSlots)
  const canActivate = availableSlots > 0
  const subscribed = usableQuota

  return {
    usedSlots,
    confirmedQuota,
    usableQuota,
    scheduledQuota,
    freeQuota,
    freeQuotaUsed,
    totalQuota,
    availableSlots,
    canActivate,
    subscribed
  }
}
